import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { Button } from "../../../components/ui/button";
import Card from "../../../components/ui/card-snippet";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PromoCodeDiscountTypeEnum,
  respEnum,
  IntMax,
  DateminFormat,
  IntMin,
  IntCourtPriceMin,
  IntMinLevel,
  RoleEnum,
  DateTimeminFormat,
  ToCSharpFormat,
} from "../../../constants/appConstant";
import {
  FormikInput,
  FormikSwitch,
  FormikSearchSelect,
  FormikDate,
  FormikSelect,
  PhoneInputField,
} from "../../../components/formik-components";
import Common from "../../../helper/common";
import { useQueryParams } from "../../../contexts/queryParamsContext";

const formValidations = yup.object({
  code: yup.string().required("Code is required"),
  discountType: yup.number().required("Discount Type is required"),
  discountValue: yup
    .number()
    .required("Discount value is required")
    .min(IntMinLevel)
    .max(IntMax),
  minOrderAmount: yup
    .number()
    .min(IntMinLevel)
    .max(IntMax)
    .required("Min Order is required"),
  maxDiscount: yup
    .number()
    .min(IntMinLevel)
    .max(IntMax)
    .test(
      "is-required-if-percentage",
      "Max Discount is required when Discount Type is Percentage",
      function (value) {
        const { discountType } = this.parent;
        if (discountType === PromoCodeDiscountTypeEnum.Percentage) {
          return value !== undefined && value !== null && value !== "";
        }
        return true;
      }
    ),
  usageLimit: yup
    .number()
    .min(IntMinLevel)
    .max(IntMax)
    .required("Usage Limit is required"),
  userUsageLimit: yup
    .number()
    .required("User Usage Limit is required")
    .min(IntMinLevel)
    .max(IntMax)
    .test(
      "is-less-than-usageLimit",
      "User usage limit cannot be greater than usage limit",
      function (value) {
        const { usageLimit } = this.parent;
        return (
          usageLimit === null || usageLimit === undefined || value <= usageLimit
        );
      }
    ),
  validFrom: yup.date().required("Valid from is required"),
  validTo: yup
    .date()
    .min(yup.ref("validFrom"), "End date cannot be before the start date")
    .required("Valid To is required"),
  // New fields - all optional
  sportId: yup.number().nullable(),
  bookingFrom: yup.date().nullable(),
  bookingTill: yup.date().nullable(),
  promocodeDays: yup.array().of(yup.number()),
  promocodeTimes: yup.array(),
  isFirstTimeOnly: yup.boolean(),
  isPlayerSpecific: yup.boolean(),
  isPublic: yup.boolean(),
  playerId: yup.number().nullable(),
});

export default function AddPromoCodes() {
  const navigate = useNavigate();
  const formikRef = useRef();
  const [roleId, setRoleId] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [sports, setSports] = useState([]);
  const [players, setPlayers] = useState([]);
  const [timeRanges, setTimeRanges] = useState([]);
  const location = useLocation();
  let isRequested = false;
  //const { id, setId } = useQueryParams();
  const id = null;

  useEffect(() => {
    (async () => {
      setRoleId(global.user.roleId);
    })();
  }, []);

  useEffect(() => {
    getClubs();
    loadSports();
    loadPlayers();
  }, []);

  //useEffect(() => {
  //    if (id) {
  //        GetPromoCodeDetail();
  //    }

  //    return () => {
  //        setId(null);
  //    }
  //}, [id]);

  const formInitialValue = {
    validFrom: id
      ? DateTimeminFormat
      : Common.moment().format(DateTimeminFormat),
    // New fields with default values
    sportId: null,
    bookingFrom: null,
    bookingTill: null,
    promocodeDays: [],
    promocodeTimes: [],
    isFirstTimeOnly: false,
    isPlayerSpecific: false,
    isPublic: false,
    playerId: null,
  };

  const getClubs = async () => {
    return new Promise(async (res) => {
      const response = await Common.ApiService.getInstance().request(
        "GetClubsDDL"
      );
      const data = [
        { id: null, name: "Select Club" },
        ...(response?.data ?? []),
      ];

      setClubs(data);
    });
  };

  const loadSports = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetClubSports",
        null,
        "GET"
      );

      if (response && response.status === respEnum.Success) {
        setSports(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load sports:", error);
    }
  };

  const loadPlayers = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetUsers",
        null,
        "GET"
      );

      if (response && response.status === respEnum.Success) {
        setPlayers(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load players:", error);
    }
  };

  // Helper functions for time ranges
  const addTimeRange = () => {
    setTimeRanges([...timeRanges, { timeFrom: "", timeTill: "" }]);
  };

  const removeTimeRange = (index) => {
    setTimeRanges(timeRanges.filter((_, i) => i !== index));
  };

  // Time validation functions
  const validateTimeFormat = (time) => {
    if (!time) return true; // Allow empty values

    const [hours, minutes] = time.split(":").map(Number);

    // Check if hours are valid (00-23)
    if (hours < 0 || hours > 23) {
      return false;
    }

    // Check if minutes are valid (00 or 30)
    if (minutes !== 0 && minutes !== 30) {
      return false;
    }

    return true;
  };

  const checkTimeOverlap = (newTimeRanges, currentIndex, field, value) => {
    const updatedRanges = [...newTimeRanges];
    updatedRanges[currentIndex][field] = value;

    // Filter out empty ranges for overlap check
    const validRanges = updatedRanges.filter(
      (range) => range.timeFrom && range.timeTill
    );

    for (let i = 0; i < validRanges.length; i++) {
      for (let j = i + 1; j < validRanges.length; j++) {
        const range1 = validRanges[i];
        const range2 = validRanges[j];

        const start1 = range1.timeFrom;
        const end1 = range1.timeTill;
        const start2 = range2.timeFrom;
        const end2 = range2.timeTill;

        // Check if ranges overlap
        if (start1 < end2 && start2 < end1) {
          return true; // Overlap found
        }
      }
    }

    return false; // No overlap
  };

  const updateTimeRange = (index, field, value) => {
    const updated = [...timeRanges];
    updated[index][field] = value;
    setTimeRanges(updated);
  };

  // Days of week for day restrictions
  const daysOfWeek = [
    { id: 0, name: "Sunday" },
    { id: 1, name: "Monday" },
    { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" },
    { id: 4, name: "Thursday" },
    { id: 5, name: "Friday" },
    { id: 6, name: "Saturday" },
  ];

  const handlePhoneBlur = async (phone, formikProps) => {
    if (!formikProps.values.phoneNum || !phone || id) {
      formikRef.current.setFieldValue("playerId", null);
      formikRef.current.setFieldValue("playerName", "");
      return;
    }
    phone = formikProps.values.phoneNum.trim();
    var phoneCode = formikProps.values.phoneCode.replace("+", "").trim();
    if (isRequested) {
      return;
    }

    isRequested = true;
    return new Promise(async (res) => {
      const response = await Common.ApiService.getInstance().request(
        `GetAppCustomer?phoneCode=${phoneCode}&phone=${phone}`
      );

      if (response?.status == respEnum.Success && response.data) {
        formikRef.current.setFieldValue("playerId", response.data.id);
        formikRef.current.setFieldValue("playerName", response.data.name);
      } else {
        formikRef.current.setFieldValue("playerId", null);
        formikRef.current.setFieldValue("playerName", "");
      }
      isRequested = false;
      setTimeout(res, 1000);
    });
  };

  const GetPromoCodeDetail = async () => {
    let response = await Common.ApiService.getInstance().request(
      `GetPromoCodeDetail?id=${id}`
    );

    if (response.status == respEnum.Success && response?.data?.length > 0) {
      let d = response?.data?.[0];
      if (formikRef.current) {
        formikRef.current.setValues(d);
      }
    }
  };

  const onSubmitData = async (d, action) => {
    debugger;
    let data = {
      ...d,
      discountType: +d.discountType,
      minOrderAmount: d.minOrderAmount ? +d.minOrderAmount : null,
      maxDiscount: d.maxDiscount ? +d.maxDiscount : null,
      discountValue: d.discountValue ? +d.discountValue : null,
      clubId: d.clubId ? +d.clubId : null,
      usageLimit: d.usageLimit ? +d.usageLimit : null,
      userUsageLimit: d.userUsageLimit ? +d.userUsageLimit : null,
      playerId: d.playerId ? +d.playerId : null,
      validFrom: Common.moment(`${d.validFrom}`).format(ToCSharpFormat),
      validTo: Common.moment(`${d.validTo}`).format(ToCSharpFormat),
      // New fields
      sportId: d.sportId ? +d.sportId : null,
      bookingFrom: d.bookingFrom
        ? Common.moment(`${d.bookingFrom}`).format(ToCSharpFormat)
        : null,
      bookingTill: d.bookingTill
        ? Common.moment(`${d.bookingTill}`).format(ToCSharpFormat)
        : null,
      promocodeDays: d.promocodeDays || [],
      promocodeTimes: timeRanges.filter((t) => t.timeFrom && t.timeTill),
      isFirstTimeOnly: d.isFirstTimeOnly || false,
      isPlayerSpecific: d.isPlayerSpecific || false,
      isPublic: d.isPublic || false,
    };

    // Validate time ranges format and overlaps
    const validTimeRanges = timeRanges.filter((t) => t.timeFrom && t.timeTill);
    if (validTimeRanges.length > 0) {
      // Check time format validation
      for (const range of validTimeRanges) {
        if (!validateTimeFormat(range.timeFrom)) {
          Common.showToast(
            `Invalid time format in "From" time. Minutes must be 00 or 30, and hours must be 00-23.`
          );
          return;
        }
        if (!validateTimeFormat(range.timeTill)) {
          Common.showToast(
            `Invalid time format in "Till" time. Minutes must be 00 or 30, and hours must be 00-23.`
          );
          return;
        }
      }

      // Check for overlaps
      for (let i = 0; i < validTimeRanges.length; i++) {
        for (let j = i + 1; j < validTimeRanges.length; j++) {
          const range1 = validTimeRanges[i];
          const range2 = validTimeRanges[j];

          const start1 = range1.timeFrom;
          const end1 = range1.timeTill;
          const start2 = range2.timeFrom;
          const end2 = range2.timeTill;

          // Check if ranges overlap
          if (start1 < end2 && start2 < end1) {
            Common.showToast(`Time ranges cannot overlap with each other.`);
            return;
          }
        }
      }
    }

    if (
      data.discountType == PromoCodeDiscountTypeEnum.Percentage &&
      data.discountValue > 100
    ) {
      Common.showToast("Percentage so discount value should be less than 100 ");
      return;
    }
    if (data.validFrom > data.validTo) {
      Common.showToast("Valid Till Date must be greater");
      return;
    }

    if (d.isClubSpecific && !d.clubId) {
      Common.showToast("Club Is Required when Club Specific");
      return;
    }

    if (d.isPlayerSpecific && !d.playerId) {
      Common.showToast("Player is required when Player Specific");
      return;
    }

    let response = await Common.ApiService.getInstance().request(
      "AddPromoCode",
      data,
      "POST"
    );
    action.setSubmitting(false);

    if (response.status == respEnum.Success) {
      Common.SweetAlert.alert(response.message);
      setTimeout(() => {
        navigate("/app/PromoCodes");
      }, 1000);
    }
  };

  const resetFields = (value, formikProps) => {
    if (+value == PromoCodeDiscountTypeEnum.Fixed_Amount) {
      //formikProps.setFieldValue("minOrderAmount", "");
      formikProps.setFieldValue("maxDiscount", "");
    }
    if (+value == PromoCodeDiscountTypeEnum.Percentage) {
      formikProps.setFieldValue("discountValue", "");
    }
  };

  return (
    <Formik
      initialValues={formInitialValue}
      validationSchema={formValidations}
      innerRef={formikRef}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={onSubmitData}
    >
      {(formikProps) => {
        formikRef.current = formikProps;

        // Handle Enter key press
        const handleKeyDown = (e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            formikProps.handleSubmit();
          }
        };

        return (
          <>
            <div className="space-y-6 mb-5" onKeyDown={handleKeyDown}>
              <Card title={"Create Promo Code"}>
                {/* Basic Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <span className="w-2 h-6 bg-blue-500 rounded-full mr-3"></span>
                    Basic Information
                  </h2>
                  <div className="md:grid md:grid-cols-10 gap-4">
                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikInput
                        formikProps={formikProps}
                        name={"code"}
                        placeholder={"Code"}
                        label={"Code"}
                        maxLength="30"
                        disabled={id}
                      />
                    </div>
                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikInput
                        formikProps={formikProps}
                        name={"validFrom"}
                        label={"Valid From"}
                        type={"datetime-local"}
                        min={Common.Utility.todayDatetime}
                        disabled={id}
                      />
                    </div>

                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikInput
                        formikProps={formikProps}
                        name={"validTo"}
                        label={"Valid To"}
                        type={"datetime-local"}
                        disabled={id}
                        min={
                          formikProps.values.validFrom ||
                          Common.Utility.todayDatetime
                        }
                      />
                    </div>

                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikSearchSelect
                        formikProps={formikProps}
                        name={"discountType"}
                        disabled={id}
                        data={[
                          {
                            id: PromoCodeDiscountTypeEnum.Percentage,
                            name: "Percentage",
                          },
                          {
                            id: PromoCodeDiscountTypeEnum.Fixed_Amount,
                            name: "Fixed Amount",
                          },
                        ]}
                        placeholder={"Select Type"}
                        label={"Discount Type"}
                        onChange={(e) => {
                          resetFields(e, formikProps);
                        }}
                      />
                    </div>
                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikInput
                        formikProps={formikProps}
                        name={"discountValue"}
                        disabled={id}
                        placeholder={"Enter Discount Value"}
                        label={"Discount Value"}
                        type={"number"}
                        max={IntMax}
                        min={IntMinLevel}
                        onChange={(value) => {
                          if (value < IntMinLevel && value != "") {
                            formikProps.setFieldValue(
                              "discountValue",
                              IntMinLevel
                            );
                          } else if (
                            formikProps.values.discountType ==
                              PromoCodeDiscountTypeEnum.Percentage &&
                            value > IntCourtPriceMin
                          ) {
                            Common.showToast(
                              `Discount value cannot be greater than ${IntCourtPriceMin}`
                            );
                            formikProps.setFieldValue("discountValue", "");
                          } else if (
                            formikProps.values.discountType !=
                              PromoCodeDiscountTypeEnum.Percentage &&
                            value > IntMax
                          ) {
                            Common.showToast(
                              `Discount value cannot be greater than ${IntMax}`
                            );
                            formikProps.setFieldValue("discountValue", "");
                          } else {
                            formikProps.setFieldValue("discountValue", value);
                          }
                        }}
                      />
                    </div>

                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikInput
                        formikProps={formikProps}
                        disabled={id}
                        name={"minOrderAmount"}
                        placeholder={"Enter Min Order Amount"}
                        label={"Min Order Amount"}
                        type={"number"}
                        max={IntMax}
                        min={IntMinLevel}
                        onChange={(value) => {
                          if (value < IntMinLevel && value != "") {
                            formikProps.setFieldValue(
                              "minOrderAmount",
                              IntMinLevel
                            );
                          } else if (value > IntMax) {
                            Common.showToast(
                              `Min Order Amount value cannot be greater than ${IntMax}`
                            );
                            formikProps.setFieldValue("minOrderAmount", "");
                          } else {
                            formikProps.setFieldValue("minOrderAmount", value);
                          }
                        }}
                      />
                    </div>

                    {formikProps.values.discountType ==
                      PromoCodeDiscountTypeEnum.Percentage && (
                      <div className="flex flex-col col-span-2 gap-2">
                        <FormikInput
                          formikProps={formikProps}
                          disabled={id}
                          name={"maxDiscount"}
                          placeholder={"Enter Max Discount "}
                          label={"Max Discount"}
                          type={"number"}
                          max={IntMax}
                          min={IntMinLevel}
                          onChange={(value) => {
                            if (value < IntMinLevel && value != "") {
                              formikProps.setFieldValue(
                                "maxDiscount",
                                IntMinLevel
                              );
                            } else if (value > IntMax) {
                              Common.showToast(
                                `Max Discount cannot be greater than ${IntMax}`
                              );
                              formikProps.setFieldValue("maxDiscount", "");
                            } else {
                              formikProps.setFieldValue("maxDiscount", value);
                            }
                          }}
                        />
                      </div>
                    )}

                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikInput
                        formikProps={formikProps}
                        disabled={id}
                        name={"usageLimit"}
                        placeholder={"Enter Limit "}
                        label={"Limit"}
                        type={"number"}
                        max={IntMax}
                        min={IntMinLevel}
                        onChange={(value) => {
                          if (value < IntMinLevel && value != "") {
                            formikProps.setFieldValue(
                              "usageLimit",
                              IntMinLevel
                            );
                          } else if (value > IntMax) {
                            Common.showToast(
                              `Limit cannot be greater than ${IntMax}`
                            );
                            formikProps.setFieldValue("usageLimit", "");
                          } else {
                            formikProps.setFieldValue("usageLimit", value);
                          }
                        }}
                      />
                    </div>
                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikInput
                        formikProps={formikProps}
                        name={"userUsageLimit"}
                        disabled={id}
                        placeholder={"Enter User Limit "}
                        label={"User Limit"}
                        type={"number"}
                        max={IntMax}
                        min={IntMinLevel}
                        onChange={(value) => {
                          if (value < IntMinLevel && value != "") {
                            formikProps.setFieldValue(
                              "userUsageLimit",
                              IntMinLevel
                            );
                          } else if (
                            formikProps.values.usageLimit != "" &&
                            value > formikProps.values.usageLimit
                          ) {
                            Common.showToast(
                              `User Limit cannot be greater than Limit`
                            );
                            formikProps.setFieldValue("userUsageLimit", "");
                          } else if (value > IntMax) {
                            Common.showToast(
                              `User Limit cannot be greater than ${IntMax}`
                            );
                            formikProps.setFieldValue("userUsageLimit", "");
                          } else {
                            formikProps.setFieldValue("userUsageLimit", value);
                          }
                        }}
                      />
                    </div>

                    <div className="flex flex-col col-span-2 gap-2">
                      <FormikSwitch
                        formikProps={formikProps}
                        disabled={id}
                        name={"isPublic"}
                        label={"Public Promocode"}
                        style={{ marginTop: "7px" }}
                      />
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                        <p className="text-xs text-blue-800 font-medium mb-1">
                          📢 Public Promocode Note:
                        </p>
                        <p className="text-xs text-blue-700">
                          If enabled, this promocode will be visible and
                          available to all customers.
                        </p>
                      </div>
                    </div>

                    {roleId && roleId == RoleEnum.Super_Admin && (
                      <>
                        <div className="flex flex-col col-span-2 gap-2">
                          <FormikSwitch
                            formikProps={formikProps}
                            disabled={id}
                            name={"isFirstTimeOnly"}
                            label={"Is First Time Only"}
                            style={{ marginTop: "7px" }}
                          />
                        </div>

                        <div className="flex flex-col col-span-2 gap-1">
                          <FormikSwitch
                            formikProps={formikProps}
                            disabled={id}
                            name={"isClubSpecific"}
                            label={"Is Club Specific"}
                            style={{ marginTop: "7px" }}
                            onChange={(e) => {
                              if (e == false)
                                formikProps.setFieldValue("clubId", "");
                            }}
                          />
                        </div>

                        <div className="flex flex-col col-span-2 gap-1">
                          <FormikSelect
                            formikProps={formikProps}
                            disabled={!formikProps.values.isClubSpecific || id}
                            name={"clubId"}
                            data={clubs}
                            placeholder={"Select Club"}
                            label={"Club"}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Player Specific Settings Section */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <span className="w-2 h-6 bg-purple-500 rounded-full mr-3"></span>
                    Player Specific Settings
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1">
                      <FormikSwitch
                        formikProps={formikProps}
                        disabled={id}
                        name={"isPlayerSpecific"}
                        label={"Player Specific"}
                        style={{ marginTop: "7px" }}
                        onChange={(e) => {
                          if (e == false) {
                            formikProps.setFieldValue("phone", "");
                            formikProps.setFieldValue("playerName", "");
                          }
                        }}
                      />
                    </div>

                    {formikProps.values.isPlayerSpecific && (
                      <>
                        <div className="flex flex-col gap-1">
                          <PhoneInputField
                            formikProps={formikProps}
                            disabled={
                              !formikProps.values.isPlayerSpecific || id
                            }
                            maxLength={30}
                            onBlur={(e) =>
                              handlePhoneBlur(e.target.value, formikProps)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handlePhoneBlur(e.target.value, formikProps);
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <FormikInput
                            formikProps={formikProps}
                            name={"playerName"}
                            label={"Player Name"}
                            disabled={true}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Restrictions Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <span className="w-2 h-6 bg-green-500 rounded-full mr-3"></span>
                    Restrictions & Conditions
                  </h2>
                  <div className="space-y-6">
                    {/* Row 1: Sport Restriction & Booking Validity Period */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Sport Restriction */}
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <span className="w-1.5 h-4 bg-orange-400 rounded-full mr-2"></span>
                          Sport Restriction
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          If no sport is selected, the promocode will be valid
                          for all sports
                        </p>
                        <div className="max-w-md">
                          <FormikSearchSelect
                            formikProps={formikProps}
                            name={"sportId"}
                            disabled={id}
                            data={sports}
                            placeholder={"Select Sport (Optional)"}
                            label={"Choose Sport"}
                            isClearable={true}
                          />
                        </div>
                      </div>

                      {/* Booking Validity Period */}
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <span className="w-1.5 h-4 bg-purple-400 rounded-full mr-2"></span>
                          Booking Validity Period
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          Set the time window when bookings can be made using
                          this promocode
                        </p>
                        <div className="space-y-4">
                          <div className="flex flex-col gap-2">
                            <FormikInput
                              formikProps={formikProps}
                              name={"bookingFrom"}
                              label={"Booking From (Optional)"}
                              type={"datetime-local"}
                              disabled={id}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <FormikInput
                              formikProps={formikProps}
                              name={"bookingTill"}
                              label={"Booking Till (Optional)"}
                              type={"datetime-local"}
                              disabled={id}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Valid Days & Valid Time Ranges */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Day Restrictions */}
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <span className="w-1.5 h-4 bg-red-400 rounded-full mr-2"></span>
                          Valid Days
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Select the days when this promocode can be used
                          (Optional)
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {daysOfWeek.map((day) => (
                            <label
                              key={day.id}
                              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formikProps.values.promocodeDays.includes(
                                  day.id
                                )}
                                onChange={(e) => {
                                  const days = [
                                    ...formikProps.values.promocodeDays,
                                  ];
                                  if (e.target.checked) {
                                    days.push(day.id);
                                  } else {
                                    const index = days.indexOf(day.id);
                                    if (index > -1) days.splice(index, 1);
                                  }
                                  formikProps.setFieldValue(
                                    "promocodeDays",
                                    days
                                  );
                                }}
                                className="rounded text-blue-600 focus:ring-blue-500"
                                disabled={id}
                              />
                              <span className="text-sm font-medium text-gray-700">
                                {day.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Time Range Restrictions */}
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <span className="w-1.5 h-4 bg-teal-400 rounded-full mr-2"></span>
                          Valid Time Ranges
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Define specific time windows when this promocode can
                          be used (Optional)
                        </p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                          <p className="text-xs text-blue-800 font-medium mb-1">
                            ⏰ Time Format Requirements:
                          </p>
                          <ul className="text-xs text-blue-700 space-y-1">
                            <li>• Hours: 00-23 (24-hour format)</li>
                            <li>• Minutes: Only 00 or 30 allowed</li>
                            <li>
                              • Time ranges cannot overlap with each other
                            </li>
                            <li>• Example: 09:00, 09:30, 14:00, 14:30</li>
                          </ul>
                        </div>
                        <div className="space-y-4">
                          <Button
                            type="button"
                            onClick={addTimeRange}
                            className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-md transition-colors w-full sm:w-auto"
                            disabled={id}
                          >
                            <span className="mr-2">+</span>
                            Add Time Range
                          </Button>

                          {timeRanges.length === 0 ? (
                            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                              <p className="text-sm">
                                No time ranges added yet
                              </p>
                              <p className="text-xs mt-1">
                                Click "Add Time Range" to create one
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                              {timeRanges.map((range, index) => (
                                <div
                                  key={index}
                                  className="flex items-center space-x-3 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200"
                                >
                                  <div className="flex items-center space-x-2 flex-1">
                                    <input
                                      type="time"
                                      value={range.timeFrom}
                                      onChange={(e) =>
                                        updateTimeRange(
                                          index,
                                          "timeFrom",
                                          e.target.value
                                        )
                                      }
                                      step="1800"
                                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                                      disabled={id}
                                      title="Time must be in 30-minute intervals (00 or 30 minutes)"
                                    />
                                    <span className="text-gray-500 font-medium text-sm">
                                      to
                                    </span>
                                    <input
                                      type="time"
                                      value={range.timeTill}
                                      onChange={(e) =>
                                        updateTimeRange(
                                          index,
                                          "timeTill",
                                          e.target.value
                                        )
                                      }
                                      step="1800"
                                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                                      disabled={id}
                                      title="Time must be in 30-minute intervals (00 or 30 minutes)"
                                    />
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => removeTimeRange(index)}
                                    className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 text-xs px-2 py-1"
                                    disabled={id}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Save Button Section */}
            <div className="mt-6">
              {/* Desktop Save Button */}
              <div className="hidden md:flex justify-end">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-1 rounded-lg">
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      formikProps.handleSubmit();
                    }}
                    disabled={formikProps.isSubmitting}
                    type="submit"
                    className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-2 font-semibold rounded-md transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {formikProps.isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>💾</span>
                        <span>Save Promo Code</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>

              {/* Mobile Save Button */}
              <div className="md:hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-1 rounded-lg">
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      formikProps.handleSubmit();
                    }}
                    disabled={formikProps.isSubmitting}
                    type="submit"
                    className="w-full bg-white text-blue-600 hover:bg-gray-50 px-6 py-3 font-semibold rounded-md transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {formikProps.isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <span>💾</span>
                        <span>Save Promo Code</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        );
      }}
    </Formik>
  );
}
